import {
  EJobRecordStatus,
  type IJobRecordMockTest,
  type IJobRecordQuestion,
  type IJobRecordStation,
} from '@quesmed/types/models';
import { useParams } from 'react-router-dom';
import type { IStationSchema } from '@/components/station/schema';
import { EFormMode } from '@/config/globals';
import { toaster } from '@/core/ui';
import {
  CreateJobRecordDocument,
  GetJobDocument,
  GetJobRecordsDocument,
  type InputMaybe,
  type JobRecordStationInput,
  UpdateJobRecordDocument,
  UpdateJobRecordStatusDocument,
} from '@/graphql/gql/graphql';
import { useMutation } from '@/libs';
import { useRecord } from '@/store/record';
import { API } from '@/utils/index-db/tables';
import type {
  IRecordMockTestSchema,
  IRecordQuestionSchema,
  IRecordSchema,
} from '../../app/(dashboard)/content/job/detail/components/record/schema';

export const useRecordActions = () => {
  const currentRecord = useRecord.use.currentRecord();
  const recordId = currentRecord?.id;
  const { id: jobId } = useParams<{ id: string }>();
  const setRecordModal = useRecord.use.setRecordModal();
  const selectedRecords = useRecord.use.selectedRecords();
  // const deletedAssets = useRecord.use.deletedAssets();
  const [createRecord, { loading: loadingCreate }] = useMutation(CreateJobRecordDocument, {
    refetchQueries: [GetJobRecordsDocument, GetJobDocument],
  });
  const [updateRecord, { loading: loadingUpdate }] = useMutation(UpdateJobRecordDocument, {
    refetchQueries: [GetJobRecordsDocument, GetJobDocument],
  });
  const [updateJobRecordStatus, { loading: loadingUpdateStatus }] = useMutation(
    UpdateJobRecordStatusDocument,
    { refetchQueries: [GetJobRecordsDocument, GetJobDocument] },
  );

  const isLoading = loadingCreate || loadingUpdate;

  const updateQuestion = async (input: Partial<IRecordSchema>, _: IRecordSchema) => {
    if (!recordId) {
      return;
    }

    const question = transformDataForBackend(input) as Partial<IJobRecordQuestion>;
    const { createdAssetIds, deletedAssetIds } = getDeletedAndCreatedAssetIds(input as IRecordSchema);

    let referenceId: string | null | undefined;
    if ('referenceId' in input) {
      referenceId = (input as IRecordQuestionSchema).referenceId || null;
    }
    toaster.toast.promise(
      async () => {
        const res = await updateRecord({
          variables: {
            input: { question },
            id: recordId,
            createdAssetIds,
            deletedAssetIds,
            referenceId,
          },
        });
        if (!res.data?.admin?.updateJobRecord) {
          throw new Error('Failed to Update the Question Job Record');
        }
        // setRecordModal(false, EFormMode.CREATE);
      },
      {
        loading: 'Updating the Question Job Record...',
        success: 'Successfully Updated the Question Job Record',
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center' },
    );
  };

  const updateStation = async (input: Partial<IRecordSchema>, _: IRecordSchema) => {
    if (!recordId) {
      return;
    }

    const station = transformStationDataForBackend(input);
    const { createdAssetIds, deletedAssetIds } = getDeletedAndCreatedAssetIds(input as IRecordSchema);

    let referenceId: string | null | undefined;
    if ('referenceId' in input) {
      referenceId = (input as IRecordQuestionSchema).referenceId || null;
    }
    toaster.toast.promise(
      async () => {
        const res = await updateRecord({
          variables: {
            input: { station },
            id: recordId,
            createdAssetIds,
            deletedAssetIds,
            referenceId,
          },
        });
        if (!res.data?.admin?.updateJobRecord) {
          throw new Error('Failed to Update the Station Job Record');
        }
        // setRecordModal(false, EFormMode.CREATE);
      },
      {
        loading: 'Updating the Station Job Record...',
        success: 'Successfully Updated the Station Job Record',
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center' },
    );
  };

  const createQuestion = async (fullData: IRecordSchema) => {
    if (!jobId) {
      return;
    }
    const assetIds = getAssetIds(fullData);
    const question = transformDataForBackend(fullData);
    const referenceId = (fullData as IRecordQuestionSchema).referenceId || null;
    toaster.toast.promise(
      async () => {
        const res = await createRecord({
          variables: {
            input: { data: { question }, jobId, referenceId, assetIds },
          } as any,
        });
        if (!res.data?.admin?.createJobRecord) {
          await API.failedRecords.create({
            jobId,
            type: 'question',
            data: fullData,
            error: res.error?.message || res.error?.toString(),
          });
          throw new Error('Failed to Create the Question Job Record');
        }
        setRecordModal(false, EFormMode.CREATE);
        return res.data.admin.createJobRecord;
      },
      {
        loading: 'Creating the Question Job Record...',
        success: (data) => `Successfully Created the Question Job Record with ID: ${data.id}`,
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center', success: { duration: 5000 } },
    );
  };

  const createStation = async (fullData: IRecordSchema) => {
    if (!jobId) {
      return;
    }
    const assetIds = getAssetIds(fullData);
    const station = transformStationDataForBackend(fullData);
    toaster.toast.promise(
      async () => {
        const res = await createRecord({
          variables: {
            input: { data: { station }, jobId, assetIds },
          } as any,
        });
        if (!res.data?.admin?.createJobRecord) {
          await API.failedRecords.create({
            jobId,
            type: 'station',
            data: fullData,
            error: res.error?.message || res.error?.toString(),
          });
          throw new Error('Failed to Create the Station Job Record');
        }
        setRecordModal(false, EFormMode.CREATE);
        return res.data.admin.createJobRecord;
      },
      {
        loading: 'Creating the Station Job Record...',
        success: (data) => `Successfully Created the Station Job Record with ID: ${data.id}`,
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center', success: { duration: 5000 } },
    );
  };

  const updateMockQuestion = async (input: Partial<IRecordSchema>, _: IRecordSchema) => {
    if (!recordId) {
      return;
    }

    const mockTest = transformMockDataForBackend(input);
    const { createdAssetIds, deletedAssetIds } = getDeletedAndCreatedAssetIds(input as IRecordSchema);
    let referenceId: string | null | undefined;
    if ('referenceId' in input) {
      referenceId = (input as IRecordQuestionSchema).referenceId || null;
    }
    toaster.toast.promise(
      async () => {
        const res = await updateRecord({
          variables: {
            input: { mockTest },
            id: recordId,
            createdAssetIds,
            deletedAssetIds,
            referenceId,
          },
        });
        if (!res.data?.admin?.updateJobRecord) {
          throw new Error('Failed to Update the Mock Test Question Job Record');
        }
        // setRecordModal(false, EFormMode.CREATE);
      },
      {
        loading: 'Updating the Mock Test Question Job Record...',
        success: 'Successfully Updated the Mock Test Question Job Record',
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center' },
    );
  };

  const createMockQuestion = async (fullData: IRecordSchema) => {
    if (!jobId) {
      return;
    }
    const assetIds = getAssetIds(fullData);
    const mockTest = transformMockDataForBackend(fullData);
    const referenceId = (fullData as IRecordMockTestSchema).referenceId || null;
    toaster.toast.promise(
      async () => {
        const res = await createRecord({
          variables: {
            input: { data: { mockTest }, jobId, referenceId, assetIds },
          } as any,
        });
        if (!res.data?.admin?.createJobRecord) {
          await API.failedRecords.create({
            jobId,
            type: 'mocktest',
            data: fullData,
            error: res.error?.message || res.error?.toString(),
          });
          throw new Error('Failed to Create the Mock Test Question Job Record');
        }
        setRecordModal(false, EFormMode.CREATE);
        return res.data.admin.createJobRecord;
      },
      {
        loading: 'Creating the Mock Test Question Job Record...',
        success: (data) => `Successfully Created the Mock Test Question Job Record with ID: ${data.id}`,
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center', success: { duration: 5000 } },
    );
  };

  const updateRecordStatus = async (status: Exclude<EJobRecordStatus, EJobRecordStatus.ALL>) => {
    if (!recordId) {
      return;
    }
    const statusMessages = {
      [EJobRecordStatus.DRAFT]: {
        loading: 'Moving the Job Record to Draft...',
        success: 'Successfully Moved the Job Record to Draft',
      },
      [EJobRecordStatus.APPROVED]: {
        loading: 'Approving the Job Record...',
        success: 'Successfully Approved the Job Record',
      },
      [EJobRecordStatus.REJECTED]: {
        loading: 'Rejecting the Job Record...',
        success: 'Successfully Rejected the Job Record',
      },
      [EJobRecordStatus.PUBLISHED]: {
        loading: 'Publishing the Job Record...',
        success: 'Successfully Published the Job Record',
      },
      [EJobRecordStatus.TESTING]: {
        loading: 'Moving the Job Record to Testing...',
        success: 'Successfully Moved the Job Record to Testing',
      },
      [EJobRecordStatus.READY]: {
        loading: 'Moving the Job Record to Ready...',
        success: 'Successfully Moved the Job Record to Ready',
      },
      [EJobRecordStatus.PAID]: {
        loading: 'Marking the Job Record as Paid...',
        success: 'Successfully Marked the Job Record as Paid',
      },
    };

    toaster.toast.promise(
      async () => {
        const res = await updateJobRecordStatus({
          variables: { ids: [recordId], status },
        });
        if (!res.data?.admin?.updateJobRecordStatus) {
          throw new Error('Failed to Update the Job Record status');
        }
        return res.data.admin.updateJobRecordStatus;
      },
      {
        loading: statusMessages[status]?.loading,
        success: statusMessages[status]?.success,
        // error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center', success: { duration: 5000 } },
    );
  };

  const updateSelectedRecordStatus = async (status: Exclude<EJobRecordStatus, EJobRecordStatus.ALL>) => {
    if (!selectedRecords.length) {
      return;
    }
    const statusMessages = {
      [EJobRecordStatus.DRAFT]: {
        loading: 'Moving the Job Record to Draft...',
        success: 'Successfully Moved the Job Record to Draft',
      },
      [EJobRecordStatus.APPROVED]: {
        loading: 'Approving the Job Record...',
        success: 'Successfully Approved the Job Record',
      },
      [EJobRecordStatus.REJECTED]: {
        loading: 'Rejecting the Job Record...',
        success: 'Successfully Rejected the Job Record',
      },
      [EJobRecordStatus.PUBLISHED]: {
        loading: 'Publishing the Job Record...',
        success: 'Successfully Published the Job Record',
      },
      [EJobRecordStatus.TESTING]: {
        loading: 'Moving the Job Record to Testing...',
        success: 'Successfully Moved the Job Record to Testing',
      },
      [EJobRecordStatus.READY]: {
        loading: 'Moving the Job Record to Ready...',
        success: 'Successfully Moved the Job Record to Ready',
      },
      [EJobRecordStatus.PAID]: {
        loading: 'Marking the Job Record as Paid...',
        success: 'Successfully Marked the Job Record as Paid',
      },
    };

    toaster.toast.promise(
      async () => {
        const res = await updateJobRecordStatus({
          variables: { ids: selectedRecords, status },
        });
        if (!res.data?.admin?.updateJobRecordStatus) {
          throw new Error('Failed to Update the Job Record status');
        }
        return res.data.admin.updateJobRecordStatus;
      },
      {
        loading: statusMessages[status]?.loading,
        success: statusMessages[status]?.success,
        error: (err: any) => `This just happened: ${err.toString()}`,
      },
      { position: 'top-center', success: { duration: 5000 } },
    );
  };

  const transformDataForBackend = <T extends Partial<IRecordSchema>>(data: T): IJobRecordQuestion => {
    const { assets, ukmlaTopicId: _ukmlaTopicId, ...rest } = data as IRecordQuestionSchema;

    if (
      rest.irtParameters &&
      (typeof rest.irtParameters.discrimination !== 'number' || !('discrimination' in rest.irtParameters))
    ) {
      delete rest.irtParameters;
    }

    // remove referenceId from the data
    rest.referenceId = undefined;

    const ukmlaTags = Object.fromEntries(
      (['presentationIds', 'conditionIds', 'presentationAreaIds', 'conditionAreaIds'] as const)
        .filter((key) => key in rest)
        .map((key) => [key, rest[key]?.map(Number) ?? []]),
    );

    // TODO:: fix this so we don't have to transform
    return {
      ...rest,
      // ...(assets?.filter((a) => !a.recordId) ? { assetIds: assets.map((a) => a.id) } : {}),
      entitlementIds: rest.entitlementIds?.length ? rest.entitlementIds.map(Number) : undefined,
      themeIds: rest.themeIds?.length ? rest.themeIds.map(Number) : undefined,
      ...ukmlaTags,
    } as any;
  };

  const transformStationDataForBackend = <T extends Partial<IRecordSchema>>(
    data: T,
  ): InputMaybe<JobRecordStationInput> => {
    const { assets, llmActorBrief: _llmActorBrief, ...rest } = data as IStationSchema;
    let marks: IJobRecordStation['marks'] | undefined;
    if (Object.hasOwn(rest, 'marks')) {
      marks = rest.marks
        .flatMap((mark) =>
          mark.choices.map((choice) => ({
            header: mark.header,
            name: choice.name,
            index: choice.index,
            weight: choice.weight ?? 1,
          })),
        )
        .sort((a, b) => a.index - b.index);
    }

    return {
      ...rest,
      marks,
      entitlementIds: rest.entitlementIds?.length ? rest.entitlementIds.map(Number) : undefined,
      topicIds: rest.topicIds?.length ? rest.topicIds.map(Number) : undefined,
    } as any;
  };

  const transformMockDataForBackend = <T extends Partial<IRecordSchema>>(data: T): IJobRecordMockTest => {
    const { assets, ...rest } = data as IRecordMockTestSchema;
    if (
      rest.irtParameters &&
      (typeof rest.irtParameters.discrimination !== 'number' || !('discrimination' in rest.irtParameters))
    ) {
      delete rest.irtParameters;
    }

    // remove referenceId from the data
    rest.referenceId = undefined;

    // TODO:: fix this so we don't have to transform
    return {
      ...rest,
      themeIds: rest.themeIds?.length ? rest.themeIds.map(Number) : undefined,
    } as any;
  };

  const getAssetIds = (data: IRecordSchema): string[] => {
    // get asset ids that are not linked to record i.e a.recordId is null
    const assetIds = data.assets?.filter((a) => !a.recordId).map((a) => a.id) ?? [];
    return assetIds;
  };

  const getDeletedAndCreatedAssetIds = (
    data: IRecordSchema,
  ): { createdAssetIds: string[]; deletedAssetIds: string[] } => {
    const { createdAssetIds, deletedAssetIds } = (data.assets ?? []).reduce(
      (acc, a) => {
        // Here sequence is important, if a.deleted is true, then a.recordId is not checked

        if (a.deleted) {
          // add to deletedAssetIds if a.deleted is true
          acc.deletedAssetIds.push(a.id);
        } else if (!a.recordId) {
          // add to createdAssetIds if not linked to record
          acc.createdAssetIds.push(a.id);
        } else if (a.recordId !== recordId) {
          // skip if a.recordId exists and it's not equal to recordId, means asset is linked to another record
          return acc;
        }

        return acc;
      },
      { createdAssetIds: [] as string[], deletedAssetIds: [] as string[] },
    );
    return { createdAssetIds, deletedAssetIds };
  };

  return {
    updateQuestion,
    updateSelectedRecordStatus,
    createQuestion,
    createStation,
    updateStation,
    isLoading,
    updateRecordStatus,
    updateMockQuestion,
    createMockQuestion,
    loadingUpdateStatus,
  };
};
